import moment from 'moment';
import logoPdf from './logoPdf';
import logoUnitPdf from './logoUnitPdf';

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function formatDateLabel(value, fallback) {
  if (!value) return fallback;
  const parsed = moment(
    value,
    ['DD/MM/YYYY', 'YYYY-MM-DD', 'DDMMYYYY', 'YYYYMMDD'],
    true
  );
  if (parsed.isValid()) return parsed.format('DD/MM/YYYY');
  if (String(value).length === 8 && /^\d+$/.test(value)) {
    const compact = moment(value, 'DDMMYYYY', true);
    if (compact.isValid()) return compact.format('DD/MM/YYYY');
  }
  return value;
}

function groupProfessionals(data) {
  const units = [];
  const unitMap = new Map();

  data.forEach((item) => {
    const unitKey = item.unit_id ?? item.unit_name ?? 'sem-unidade';
    if (!unitMap.has(unitKey)) {
      const unit = {
        id: unitKey,
        name: item.unit_name || 'Unidade não informada',
        groups: [],
        groupMap: new Map(),
        count: 0,
      };
      unitMap.set(unitKey, unit);
      units.push(unit);
    }

    const unit = unitMap.get(unitKey);
    const groupKey = item.group_id ?? item.group_name ?? 'sem-grupo';
    if (!unit.groupMap.has(groupKey)) {
      const group = {
        id: groupKey,
        name: item.group_name || 'Categoria não informada',
        professionals: [],
      };
      unit.groupMap.set(groupKey, group);
      unit.groups.push(group);
    }

    unit.groupMap.get(groupKey).professionals.push(item);
    unit.count += 1;
  });

  return units;
}

function buildStyles() {
  return `
    <style>
      * { box-sizing: border-box; }

      body {
        margin: 0;
        padding: 24px 28px 40px;
        color: #1b2a41;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
        font-size: 12px;
        line-height: 1.45;
        background: #fff;
      }

      .doc-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 16px;
        padding-bottom: 16px;
        border-bottom: 1px solid #e3e7ed;
        margin-bottom: 18px;
      }

      .doc-header__logos {
        display: flex;
        align-items: center;
        gap: 16px;
      }

      .doc-title {
        margin: 0 0 4px;
        font-size: 18px;
        font-weight: 700;
        color: #0e3a66;
        letter-spacing: -0.02em;
      }

      .doc-subtitle {
        margin: 0;
        font-size: 12px;
        color: #6b7785;
        max-width: 280px;
        text-align: right;
      }

      .summary {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 10px;
        margin-bottom: 10px;
      }

      .summary__item {
        border: 1px solid #e3e7ed;
        border-radius: 8px;
        padding: 10px 12px;
        background: #fafbfc;
      }

      .summary__item--highlight {
        background: #f0f6fb;
        border-color: #c8d9ea;
      }

      .summary__label {
        display: block;
        margin-bottom: 4px;
        font-size: 10px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        color: #8b95a5;
      }

      .summary__value {
        display: block;
        font-size: 13px;
        font-weight: 600;
        color: #1b2a41;
        word-break: break-word;
      }

      .summary-units {
        width: 100%;
        margin-bottom: 22px;
        padding: 8px 12px;
        border: 1px solid #e3e7ed;
        border-radius: 8px;
        background: #fafbfc;
      }

      .summary-units__label {
        display: block;
        margin-bottom: 4px;
        font-size: 10px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        color: #8b95a5;
      }

      .summary-units__value {
        display: block;
        font-size: 12px;
        font-weight: 500;
        color: #1b2a41;
        line-height: 1.4;
      }

      .unit-block {
        page-break-inside: avoid;
        margin-bottom: 22px;
      }

      .unit-header {
        display: flex;
        justify-content: space-between;
        align-items: baseline;
        gap: 12px;
        margin-bottom: 8px;
        padding-bottom: 6px;
        border-bottom: 2px solid #0e3a66;
      }

      .unit-header__name {
        margin: 0;
        font-size: 14px;
        font-weight: 700;
        color: #0e3a66;
      }

      .unit-header__count {
        font-size: 11px;
        font-weight: 600;
        color: #6b7785;
        white-space: nowrap;
      }

      .group-title {
        margin: 14px 0 6px;
        font-size: 11px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.03em;
        color: #4a5568;
      }

      table.professionals {
        width: 100%;
        border-collapse: collapse;
        margin-bottom: 4px;
      }

      table.professionals th,
      table.professionals td {
        padding: 7px 10px;
        text-align: left;
        border-bottom: 1px solid #eef1f5;
        vertical-align: top;
      }

      table.professionals th {
        font-size: 10px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.03em;
        color: #8b95a5;
        background: #f7f9fb;
        border-bottom: 1px solid #e3e7ed;
      }

      table.professionals td {
        font-size: 12px;
        color: #1b2a41;
      }

      table.professionals tr:nth-child(even) td {
        background: #fafbfc;
      }

      .col-index {
        width: 40px;
        color: #8b95a5 !important;
        font-variant-numeric: tabular-nums;
      }

      .col-specialty {
        width: 34%;
      }

      .doc-footer {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 16px;
        margin-top: 28px;
        padding-top: 14px;
        border-top: 1px solid #e3e7ed;
      }

      .doc-footer__note {
        margin: 0;
        font-size: 11px;
        color: #8b95a5;
        max-width: 70%;
      }

      .doc-footer__total {
        text-align: right;
      }

      .doc-footer__total-label {
        display: block;
        font-size: 10px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        color: #8b95a5;
      }

      .doc-footer__total-value {
        display: block;
        margin-top: 2px;
        font-size: 20px;
        font-weight: 700;
        color: #0e3a66;
        font-variant-numeric: tabular-nums;
      }

      @media print {
        body { padding: 12px 16px 24px; }
        .unit-block { break-inside: avoid; }
      }
    </style>
  `;
}

export default function Index(
  dataExportPdf,
  formatDateStart,
  totalExportPdf,
  logo,
  unitsNames,
  dateStart,
  dateEnd
) {
  const data = Array.isArray(dataExportPdf) ? dataExportPdf : [];
  const companyName = data[0]?.company_name || 'Empresa não informada';
  const fallbackEnd = formatDateStart || moment().format('DD/MM/YYYY');
  const fallbackStart = moment().add(-29, 'days').format('DD/MM/YYYY');
  const periodStart = formatDateLabel(dateStart, fallbackStart);
  const periodEnd = formatDateLabel(dateEnd, fallbackEnd);
  const units = groupProfessionals(data);
  const uniqueUnitNames = [
    ...new Set(units.map((unit) => unit.name).filter(Boolean)),
  ];
  let unitsLabel = 'Todas as unidades';
  if (unitsNames && typeof unitsNames === 'string') {
    unitsLabel = unitsNames;
  } else if (uniqueUnitNames.length > 0) {
    unitsLabel = uniqueUnitNames.join(', ');
  }
  const total = totalExportPdf ?? data.length;

  const unitsHtml = units
    .map((unit) => {
      let professionalIndex = 0;
      const groupsHtml = unit.groups
        .map((group) => {
          const rows = group.professionals
            .map((item) => {
              professionalIndex += 1;
              return `
                <tr>
                  <td class="col-index">${professionalIndex}</td>
                  <td class="col-specialty">${escapeHtml(
                    item.specialty_name || '—'
                  )}</td>
                  <td>${escapeHtml(item.user_name || '—')}</td>
                </tr>
              `;
            })
            .join('');

          return `
            <h3 class="group-title">${escapeHtml(group.name)}</h3>
            <table class="professionals">
              <thead>
                <tr>
                  <th class="col-index">#</th>
                  <th class="col-specialty">Especialidade / Tipo</th>
                  <th>Profissional</th>
                </tr>
              </thead>
              <tbody>
                ${rows}
              </tbody>
            </table>
          `;
        })
        .join('');

      return `
        <section class="unit-block">
          <div class="unit-header">
            <h2 class="unit-header__name">${escapeHtml(unit.name)}</h2>
            <span class="unit-header__count">${unit.count} profissional${
        unit.count === 1 ? '' : 'is'
      }</span>
          </div>
          ${groupsHtml}
        </section>
      `;
    })
    .join('');

  return `
    ${buildStyles()}
    <header class="doc-header">
      <div class="doc-header__logos">
        ${logoPdf()}
        ${logo ? logoUnitPdf(logo) : ''}
      </div>
      <div>
        <h1 class="doc-title">Relatório para NFS-e</h1>
        <p class="doc-subtitle">
          Lista de profissionais por unidade para emissão de notas fiscais de serviço.
        </p>
      </div>
    </header>

    <section class="summary">
      <div class="summary__item">
        <span class="summary__label">Período</span>
        <span class="summary__value">${escapeHtml(periodStart)} a ${escapeHtml(
    periodEnd
  )}</span>
      </div>
      <div class="summary__item">
        <span class="summary__label">Empresa</span>
        <span class="summary__value">${escapeHtml(companyName)}</span>
      </div>
      <div class="summary__item summary__item--highlight">
        <span class="summary__label">Total de profissionais</span>
        <span class="summary__value">${escapeHtml(total)}</span>
      </div>
    </section>

    <div class="summary-units">
      <span class="summary-units__label">Unidades (${
        uniqueUnitNames.length || 0
      })</span>
      <span class="summary-units__value" title="${escapeHtml(
        unitsLabel
      )}">${escapeHtml(unitsLabel)}</span>
    </div>

    ${unitsHtml}

    <footer class="doc-footer">
      <p class="doc-footer__note">
        Documento auxiliar para emissão de NFS-e aos hospitais. Gerado em ${escapeHtml(
          moment().format('DD/MM/YYYY [às] HH:mm')
        )}.
      </p>
      <div class="doc-footer__total">
        <span class="doc-footer__total-label">Total geral</span>
        <span class="doc-footer__total-value">${escapeHtml(total)}</span>
      </div>
    </footer>
  `;
}
